import { useState, useEffect } from "react";
import {
  Activity,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Phone,
  MapPin,
  Heart,
  CheckCircle,
  Clock,
} from "lucide-react";
import { STORAGE_KEYS } from "../../services/api";

const CampaignDonationScreen = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");

  useEffect(() => {
    loadCampaignDonations();
  }, []);

  const loadCampaignDonations = async () => {
    try {
      setLoading(true);
      // TODO: Implement API call to fetch campaign donations
      // For now, using mock data
      const mockDonations = [
        {
          id: 1,
          donorName: "Ram Shrestha",
          bloodGroup: "A+",
          campaignName: "Blood Donation Camp - Kathmandu",
          campaignLocation: "Kathmandu Medical College",
          donationDate: "2024-01-20",
          donationTime: "10:30 AM",
          status: "Completed",
          contactNumber: "+977-9841234567",
          age: 28,
          gender: "Male",
          hemoglobin: "14.2",
          bloodPressure: "120/80",
          pulse: "72",
          temperature: "98.6",
          notes: "Healthy donor, no complications",
        },
        {
          id: 2,
          donorName: "Sita Thapa",
          bloodGroup: "O-",
          campaignName: "Emergency Blood Drive - Lalitpur",
          campaignLocation: "Patan Hospital",
          donationDate: "2024-01-18",
          donationTime: "02:15 PM",
          status: "Completed",
          contactNumber: "+977-9841234568",
          age: 32,
          gender: "Female",
          hemoglobin: "13.8",
          bloodPressure: "118/75",
          pulse: "68",
          temperature: "98.4",
          notes: "Regular donor, smooth donation",
        },
        {
          id: 3,
          donorName: "Hari Basnet",
          bloodGroup: "B+",
          campaignName: "Community Blood Donation - Bhaktapur",
          campaignLocation: "Bhaktapur Red Cross",
          donationDate: "2024-01-15",
          donationTime: "11:45 AM",
          status: "Completed",
          contactNumber: "+977-9841234569",
          age: 25,
          gender: "Male",
          hemoglobin: "15.1",
          bloodPressure: "125/82",
          pulse: "70",
          temperature: "98.7",
          notes: "First-time donor, nervous but cooperative",
        },
        {
          id: 4,
          donorName: "Anjali Karki",
          bloodGroup: "AB+",
          campaignName: "Corporate Blood Drive - Baneshwor",
          campaignLocation: "Nepal Bank Limited",
          donationDate: "2024-01-25",
          donationTime: "03:20 PM",
          status: "Scheduled",
          contactNumber: "+977-9841234570",
          age: 29,
          gender: "Female",
          hemoglobin: "13.5",
          bloodPressure: "115/78",
          pulse: "65",
          temperature: "98.5",
          notes: "Pre-screening completed, ready for donation",
        },
        {
          id: 5,
          donorName: "Bikash Tamang",
          bloodGroup: "O+",
          campaignName: "Blood Donation Camp - Kathmandu",
          campaignLocation: "Kathmandu Medical College",
          donationDate: "2024-01-20",
          donationTime: "09:15 AM",
          status: "Rejected",
          contactNumber: "+977-9841234571",
          age: 35,
          gender: "Male",
          hemoglobin: "11.8",
          bloodPressure: "140/90",
          pulse: "85",
          temperature: "99.1",
          notes: "Low hemoglobin, high blood pressure - deferred",
        },
      ];
      setDonations(mockDonations);
    } catch (error) {
      console.error("Failed to load campaign donations:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return CheckCircle;
      case "scheduled":
        return Calendar;
      case "rejected":
        return Clock;
      case "pending":
        return Clock;
      default:
        return Clock;
    }
  };

  const filteredDonations = donations.filter((donation) => {
    const matchesSearch =
      donation.donorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      donation.campaignName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      donation.bloodGroup.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" ||
      donation.status.toLowerCase() === filterStatus.toLowerCase();
    const matchesCampaign =
      filterCampaign === "all" || donation.campaignName === filterCampaign;
    return matchesSearch && matchesStatus && matchesCampaign;
  });

  const uniqueCampaigns = [...new Set(donations.map((d) => d.campaignName))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign donations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Campaign Donations
          </h1>
          <p className="text-gray-600">
            Track and manage donations from blood donation campaigns
          </p>
        </div>
        <button className="btn-primary flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add Donation
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by donor name, campaign, or blood group..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="scheduled">Scheduled</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={filterCampaign}
              onChange={(e) => setFilterCampaign(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Campaigns</option>
              {uniqueCampaigns.map((campaign) => (
                <option key={campaign} value={campaign}>
                  {campaign}
                </option>
              ))}
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Donations List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Donor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blood Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Health Metrics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDonations.map((donation) => {
                const StatusIcon = getStatusIcon(donation.status);
                return (
                  <tr key={donation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {donation.donorName}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {donation.contactNumber}
                          </div>
                          <div className="text-xs text-gray-400">
                            {donation.age} years, {donation.gender}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {donation.bloodGroup}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {donation.campaignName}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {donation.campaignLocation}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {new Date(donation.donationDate).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {donation.donationTime}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          donation.status
                        )}`}
                      >
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {donation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Hb: {donation.hemoglobin} g/dL</div>
                        <div>BP: {donation.bloodPressure} mmHg</div>
                        <div>Pulse: {donation.pulse} bpm</div>
                        <div>Temp: {donation.temperature}°F</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button className="text-primary-600 hover:text-primary-900">
                          View
                        </button>
                        {donation.status === "Scheduled" && (
                          <>
                            <button className="text-green-600 hover:text-green-900">
                              Complete
                            </button>
                            <button className="text-red-600 hover:text-red-900">
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredDonations.length === 0 && (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">
              No campaign donations found
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {searchTerm || filterStatus !== "all" || filterCampaign !== "all"
                ? "Try adjusting your search or filter criteria"
                : "No donations have been recorded yet"}
            </p>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Donations
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {donations.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {donations.filter((d) => d.status === "Completed").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">
                {donations.filter((d) => d.status === "Scheduled").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-100">
              <Heart className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">
                {donations.filter((d) => d.status === "Rejected").length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDonationScreen;
